import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, {
  DocumentSignSigningSessionResponse,
  DocumentSignSigningFieldSubmission,
  DocumentSignDeclineKind,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/**
 * Bootstraps the signing session — single fetch that powers the entire
 * SigningPage. Token comes straight from the URL; we never persist it.
 */
export function useSigningSession(token: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.documentSign.signingSession(token ?? '__none__'),
    queryFn: () => api.getDocumentSignSigningSession(token as string),
    enabled: !!token,
    // Recipient view should never refetch silently — every refetch costs the
    // recipient a `RecipientViewed` event with their IP/UA. Fresh-once.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
}

export function useAcceptConsent(token: string) {
  const qc = useQueryClient();
  return useMutation({
    /**
     * `disclosureVersion` is what the page rendered. Sending it lets the
     * server refuse an acceptance against text that was superseded between
     * page load and click.
     */
    mutationFn: (disclosureVersion?: number) =>
      api.acceptDocumentSignConsent(token, disclosureVersion),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documentSign.signingSession(token) });
    },
  });
}

/**
 * Second factor. On success the session query is invalidated so the page
 * re-requests it — the server now resolves the token past the code check.
 */
export function useVerifyAccessCode(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.verifyDocumentSignAccessCode(token, code),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documentSign.signingSession(token) });
    },
  });
}

export function useSubmitFields(token: string) {
  return useMutation({
    mutationFn: (fields: DocumentSignSigningFieldSubmission[]) =>
      api.submitDocumentSignFields(token, { fields }),
  });
}

export function useSubmitSignature(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.submitDocumentSignSubmit(token),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documentSign.signingSession(token) });
    },
  });
}

export function useDeclineSignature(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: string | { reason: string; declineKind: DocumentSignDeclineKind }) =>
      typeof input === 'string'
        ? api.declineDocumentSignSignature(token, input)
        : api.declineDocumentSignSignature(token, input.reason, input.declineKind),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documentSign.signingSession(token) });
    },
  });
}

/**
 * Lets a signer who is a logged-in Fias user choose a Vault holder to receive
 * their completed signed copy. This is an AUTHENTICATED call — distinct from
 * the public token surface — because the holder choice must be validated
 * against a real Vault principal. Optional: a signer can skip it and still
 * sign; external email-only signers never see this.
 */
export function useSetDeliveryHolder(recipientId: string) {
  return useMutation({
    mutationFn: (holderId: string) => api.setDocumentSignDeliveryHolder(recipientId, holderId),
  });
}

export type SigningSessionData = DocumentSignSigningSessionResponse;

/**
 * Branding for a signing link, fetched independently of the session.
 *
 * Needed because a recipient returning after signing gets a terminal response
 * from the session endpoint and so never receives the branding carried on it.
 * `enabled` lets callers skip the request when the session already supplied it.
 */
export function useSigningBranding(token: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.documentSign.signingBranding(token),
    queryFn: () => api.getDocumentSignSigningBranding(token),
    enabled: (options.enabled ?? true) && token.length > 0,
    staleTime: 5 * 60 * 1000,
  });
}
